import React, { useState, useEffect } from 'react';
import {
  ShieldCheck,
  Plane,
  Mail,
  GitBranch,
  History,
  Plus,
  Trash2,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Search,
  Check,
  X
} from 'lucide-react';
import {
  adminFunctionsService,
  SystemRole,
  UserWithRoles,
  AircraftRegistration,
  DistributionGroup,
  SystemAdminAuditLog
} from './adminFunctionsService';
import { useTableSort, SortableTh } from '../../shared/useTableSort';

export const AdminControlTowerWorkspace: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'roles' | 'aircraft' | 'distribution' | 'reporting' | 'audit'>('roles');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // 1. Roles & Users State
  const [roles, setRoles] = useState<SystemRole[]>([]);
  const [users, setUsers] = useState<UserWithRoles[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserWithRoles | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', email: '', first_name: '', last_name: '', roles: [] as string[] });

  // 2. Aircraft State
  const [aircraftList, setAircraftList] = useState<AircraftRegistration[]>([]);
  const [showAddAircraftModal, setShowAddAircraftModal] = useState(false);
  const [newAircraft, setNewAircraft] = useState({ registration: '', aircraft_type: '', serial_number: '', operator_name: '' });

  // 3. Distribution Groups State
  const [distGroups, setDistGroups] = useState<DistributionGroup[]>([]);
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', description: '' });

  // 4. Reporting Tools State
  const [relateForm, setRelateForm] = useState({ master_report_id: '', related_report_id: '', reason_comment: '' });
  const [copyForm, setCopyForm] = useState({ source_classification_id: '', target_classification_id: '' });
  const [closureOption, setClosureOption] = useState<number>(1);
  const [closureConfig, setClosureConfig] = useState<any>(null);

  // 5. Audit Log State
  const [auditLogs, setAuditLogs] = useState<SystemAdminAuditLog[]>([]);

  const { sortedItems: sortedUsers, sortField: userSortField, sortDirection: userSortDir, toggleSort: toggleUserSort } = useTableSort(users);
  const { sortedItems: sortedAircraft, sortField: acSortField, sortDirection: acSortDir, toggleSort: toggleAcSort } = useTableSort(aircraftList);
  const { sortedItems: sortedDistGroups, sortField: dgSortField, sortDirection: dgSortDir, toggleSort: toggleDgSort } = useTableSort(distGroups);
  const { sortedItems: sortedAdminAudit, sortField: auditSortField, sortDirection: auditSortDir, toggleSort: toggleAuditSort } = useTableSort(auditLogs);

  useEffect(() => {
    loadTabContent();
  }, [activeTab]);

  const showNotification = (msg: string, isError = false) => {
    if (isError) {
      setErrorMsg(msg);
      setTimeout(() => setErrorMsg(null), 5000);
    } else {
      setSuccessMsg(msg);
      setTimeout(() => setSuccessMsg(null), 4000);
    }
  };

  const loadTabContent = async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      if (activeTab === 'roles') {
        const [rData, uData] = await Promise.all([
          adminFunctionsService.getRoles(),
          adminFunctionsService.getUsers(userSearch),
        ]);
        setRoles(rData || []);
        setUsers(uData || []);
      } else if (activeTab === 'aircraft') {
        const aData = await adminFunctionsService.getAircraft();
        setAircraftList(aData || []);
      } else if (activeTab === 'distribution') {
        const dData = await adminFunctionsService.getDistributionGroups();
        setDistGroups(dData || []);
      } else if (activeTab === 'reporting') {
        const cfg = await adminFunctionsService.getClosureConfiguration();
        setClosureConfig(cfg);
        if (cfg?.configuration?.active_option) {
          setClosureOption(Number(cfg.configuration.active_option));
        }
      } else if (activeTab === 'audit') {
        const logs = await adminFunctionsService.getAuditLogs();
        setAuditLogs(logs || []);
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to load administrative data');
    } finally {
      setLoading(false);
    }
  };

  // Role Management Handlers
  const openRoleModal = (u: UserWithRoles) => {
    setSelectedUser(u);
    setSelectedRoles(u.roles || []);
    setShowRoleModal(true);
  };

  const toggleRoleSelection = (roleKey: string) => {
    if (selectedRoles.includes(roleKey)) {
      setSelectedRoles(selectedRoles.filter((r) => r !== roleKey));
    } else {
      setSelectedRoles([...selectedRoles, roleKey]);
    }
  };

  const handleSaveRoles = async () => {
    if (!selectedUser) return;
    try {
      setLoading(true);
      await adminFunctionsService.assignUserRoles(selectedUser.id, selectedRoles);
      showNotification(`Roles successfully updated for ${selectedUser.username}`);
      setShowRoleModal(false);
      await loadTabContent();
    } catch (e: any) {
      showNotification(e.message || 'Failed to assign roles', true);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await adminFunctionsService.createUser(newUser);
      showNotification(`User ${newUser.username} created successfully`);
      setShowCreateUserModal(false);
      setNewUser({ username: '', email: '', first_name: '', last_name: '', roles: [] });
      await loadTabContent();
    } catch (e: any) {
      showNotification(e.message || 'Failed to create user', true);
    } finally {
      setLoading(false);
    }
  };

  // Aircraft Handlers
  const handleCreateAircraft = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await adminFunctionsService.createAircraft(newAircraft);
      showNotification(`Aircraft ${newAircraft.registration} registered successfully`);
      setShowAddAircraftModal(false);
      setNewAircraft({ registration: '', aircraft_type: '', serial_number: '', operator_name: '' });
      await loadTabContent();
    } catch (e: any) {
      showNotification(e.message || 'Failed to register aircraft', true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateAircraft = async (reg: string) => {
    if (!confirm(`Are you sure you want to deactivate aircraft ${reg}? (Soft delete preserving historical audits)`)) return;
    try {
      setLoading(true);
      await adminFunctionsService.softDeleteAircraft(reg);
      showNotification(`Aircraft ${reg} deactivated.`);
      await loadTabContent();
    } catch (e: any) {
      showNotification(e.message || 'Failed to deactivate aircraft', true);
    } finally {
      setLoading(false);
    }
  };

  // Distribution Group Handlers
  const handleCreateDistGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await adminFunctionsService.createDistributionGroup(newGroup);
      showNotification(`Distribution group '${newGroup.name}' created`);
      setShowAddGroupModal(false);
      setNewGroup({ name: '', description: '' });
      await loadTabContent();
    } catch (e: any) {
      showNotification(e.message || 'Failed to create distribution group', true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateDistGroup = async (id: number, name: string) => {
    if (!confirm(`Deactivate distribution group '${name}'?`)) return;
    try {
      setLoading(true);
      await adminFunctionsService.softDeleteDistributionGroup(id);
      showNotification(`Distribution group '${name}' deactivated.`);
      await loadTabContent();
    } catch (e: any) {
      showNotification(e.message || 'Failed to deactivate group', true);
    } finally {
      setLoading(false);
    }
  };

  // Reporting Tools Handlers
  const handleRelateReports = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await adminFunctionsService.relateReports(
        relateForm.master_report_id,
        relateForm.related_report_id,
        relateForm.reason_comment
      );
      showNotification(`Report #${res.related_report_id} linked as child to Master #${res.master_report_id}`);
      setRelateForm({ master_report_id: '', related_report_id: '', reason_comment: '' });
    } catch (e: any) {
      showNotification(e.message || 'Failed to link reports (Circular relations blocked)', true);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyClassification = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await adminFunctionsService.copyClassification(
        Number(copyForm.source_classification_id),
        Number(copyForm.target_classification_id)
      );
      showNotification('Taxonomy settings, ADREP, ECCAIRS and flags cloned successfully');
      setCopyForm({ source_classification_id: '', target_classification_id: '' });
    } catch (e: any) {
      showNotification(e.message || 'Failed to copy classification settings', true);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClosureOption = async () => {
    try {
      setLoading(true);
      await adminFunctionsService.updateClosureConfiguration(closureOption);
      showNotification(`Report closure configuration updated to Option ${closureOption}`);
      await loadTabContent();
    } catch (e: any) {
      showNotification(e.message || 'Failed to update closure configuration', true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: '20px' }}>
      {/* Sub navigation pills */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '2px solid #e2e8f0', paddingBottom: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button
          onClick={() => setActiveTab('roles')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            border: 'none',
            background: activeTab === 'roles' ? '#2563eb' : '#f1f5f9',
            color: activeTab === 'roles' ? '#ffffff' : '#475569',
          }}
        >
          <ShieldCheck size={16} /> 19 System Roles & RBAC
        </button>
        <button
          onClick={() => setActiveTab('aircraft')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            border: 'none',
            background: activeTab === 'aircraft' ? '#2563eb' : '#f1f5f9',
            color: activeTab === 'aircraft' ? '#ffffff' : '#475569',
          }}
        >
          <Plane size={16} /> Aircraft Registrations ({aircraftList.length})
        </button>
        <button
          onClick={() => setActiveTab('distribution')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            border: 'none',
            background: activeTab === 'distribution' ? '#2563eb' : '#f1f5f9',
            color: activeTab === 'distribution' ? '#ffffff' : '#475569',
          }}
        >
          <Mail size={16} /> Distribution Groups ({distGroups.length})
        </button>
        <button
          onClick={() => setActiveTab('reporting')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            border: 'none',
            background: activeTab === 'reporting' ? '#2563eb' : '#f1f5f9',
            color: activeTab === 'reporting' ? '#ffffff' : '#475569',
          }}
        >
          <GitBranch size={16} /> Reporting Tools & Closure Rules
        </button>
        <button
          onClick={() => setActiveTab('audit')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            border: 'none',
            background: activeTab === 'audit' ? '#2563eb' : '#f1f5f9',
            color: activeTab === 'audit' ? '#ffffff' : '#475569',
          }}
        >
          <History size={16} /> Admin Audit Trail ({auditLogs.length})
        </button>
      </div>

      {/* Notifications */}
      {successMsg && (
        <div style={{ background: '#ecfdf5', color: '#065f46', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CheckCircle size={18} /> {successMsg}
        </div>
      )}
      {errorMsg && (
        <div style={{ background: '#fef2f2', color: '#991b1b', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertTriangle size={18} /> {errorMsg}
        </div>
      )}

      {/* TAB 1: 19 System Roles & User Role Assignment */}
      {activeTab === 'roles' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px' }}>
          {/* Roles Catalog */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '18px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>
              Canonical Aviation Roles ({roles.length})
            </h3>
            <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 14px 0' }}>
              Standard system roles strictly governing Report Validation, Risk Evaluation, CAP Investigation and Quality Audits.
            </p>
            <div style={{ maxHeight: '450px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {roles.map((r) => (
                <div key={r.roleKey} style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontWeight: 600, fontSize: '13px', color: '#1e293b' }}>{r.name}</span>
                    <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', background: '#e0e7ff', color: '#3730a3', fontWeight: 700 }}>
                      {r.module}
                    </span>
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace' }}>{r.roleKey}</div>
                </div>
              ))}
            </div>
          </div>

          {/* User Role Assignments */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>
                  User Role Allocations & RBAC Control
                </h3>
                <span style={{ fontSize: '12px', color: '#64748b' }}>Restricted to ROLE_SYSTEM_ADMIN</span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setShowCreateUserModal(true)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                >
                  <Plus size={14} /> + New User
                </button>
              </div>
            </div>

            <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#475569', position: 'sticky', top: 0, zIndex: 10 }}>
                    <SortableTh field="username" currentField={userSortField} currentDirection={userSortDir} onSort={toggleUserSort} style={{ padding: '10px' }}>
                      User
                    </SortableTh>
                    <SortableTh field="email" currentField={userSortField} currentDirection={userSortDir} onSort={toggleUserSort} style={{ padding: '10px' }}>
                      Email
                    </SortableTh>
                    <th style={{ padding: '10px' }}>Active Roles</th>
                    <th style={{ padding: '10px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.map((u) => (
                    <tr key={u.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px', fontWeight: 600, color: '#0f172a' }}>{u.username}</td>
                      <td style={{ padding: '10px', color: '#64748b' }}>{u.email}</td>
                      <td style={{ padding: '10px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {(u.roles || []).length > 0 ? (
                            u.roles.map((rk) => (
                              <span key={rk} style={{ fontSize: '11px', background: '#e2e8f0', color: '#334155', padding: '2px 6px', borderRadius: '4px' }}>
                                {rk}
                              </span>
                            ))
                          ) : (
                            <span style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '12px' }}>No roles assigned</span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '10px', textAlign: 'right' }}>
                        <button
                          onClick={() => openRoleModal(u)}
                          style={{ padding: '4px 10px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                        >
                          Assign Roles
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: Aircraft Fleet */}
      {activeTab === 'aircraft' && (
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>
                Fleet & Aircraft Registrations Master Catalog
              </h3>
              <span style={{ fontSize: '12px', color: '#64748b' }}>Authoritative registry referenced by Ingestion, Occurrence and Flight operations</span>
            </div>
            <button
              onClick={() => setShowAddAircraftModal(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
            >
              <Plus size={14} /> Register Aircraft
            </button>
          </div>

          <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#475569', position: 'sticky', top: 0, zIndex: 10 }}>
                  <SortableTh field="registration" currentField={acSortField} currentDirection={acSortDir} onSort={toggleAcSort} style={{ padding: '10px' }}>
                    Registration
                  </SortableTh>
                  <SortableTh field="aircraftType" currentField={acSortField} currentDirection={acSortDir} onSort={toggleAcSort} style={{ padding: '10px' }}>
                    Type / Airframe
                  </SortableTh>
                  <SortableTh field="serialNumber" currentField={acSortField} currentDirection={acSortDir} onSort={toggleAcSort} style={{ padding: '10px' }}>
                    Serial Number
                  </SortableTh>
                  <SortableTh field="operatorName" currentField={acSortField} currentDirection={acSortDir} onSort={toggleAcSort} style={{ padding: '10px' }}>
                    Operator
                  </SortableTh>
                  <SortableTh field="isActive" currentField={acSortField} currentDirection={acSortDir} onSort={toggleAcSort} style={{ padding: '10px' }}>
                    Status
                  </SortableTh>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedAircraft.map((ac) => (
                <tr key={ac.registration} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px', fontWeight: 700, color: '#1e40af' }}>{ac.registration}</td>
                  <td style={{ padding: '10px' }}>{ac.aircraftType}</td>
                  <td style={{ padding: '10px', color: '#64748b' }}>{ac.serialNumber || '—'}</td>
                  <td style={{ padding: '10px' }}>{ac.operatorName || 'Standard Fleet'}</td>
                  <td style={{ padding: '10px' }}>
                    {ac.isActive ? (
                      <span style={{ background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}>Active</span>
                    ) : (
                      <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}>Deactivated</span>
                    )}
                  </td>
                  <td style={{ padding: '10px', textAlign: 'right' }}>
                    {ac.isActive && (
                      <button
                        onClick={() => handleDeactivateAircraft(ac.registration)}
                        style={{ padding: '4px 8px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}
                      >
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* TAB 3: Distribution Groups */}
      {activeTab === 'distribution' && (
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>
                Notification Distribution Groups & Report Routing
              </h3>
              <span style={{ fontSize: '12px', color: '#64748b' }}>Dual-listbox user memberships and report kind subscriptions</span>
            </div>
            <button
              onClick={() => setShowAddGroupModal(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
            >
              <Plus size={14} /> Create Distribution Group
            </button>
          </div>

          <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#475569', position: 'sticky', top: 0, zIndex: 10 }}>
                  <SortableTh field="name" currentField={dgSortField} currentDirection={dgSortDir} onSort={toggleDgSort} style={{ padding: '10px' }}>
                    Group Name
                  </SortableTh>
                  <SortableTh field="description" currentField={dgSortField} currentDirection={dgSortDir} onSort={toggleDgSort} style={{ padding: '10px' }}>
                    Description
                  </SortableTh>
                  <th style={{ padding: '10px' }}>Assigned Members</th>
                  <th style={{ padding: '10px' }}>Subscribed Report Kinds</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedDistGroups.map((g) => (
                <tr key={g.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px', fontWeight: 600, color: '#0f172a' }}>{g.name}</td>
                  <td style={{ padding: '10px', color: '#64748b' }}>{g.description || '—'}</td>
                  <td style={{ padding: '10px' }}>
                    <span style={{ background: '#e0e7ff', color: '#3730a3', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600 }}>
                      {g.memberUserIds?.length || 0} Members
                    </span>
                  </td>
                  <td style={{ padding: '10px' }}>
                    <span style={{ background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600 }}>
                      {g.reportKindIds?.length || 0} Kinds
                    </span>
                  </td>
                  <td style={{ padding: '10px', textAlign: 'right' }}>
                    <button
                      onClick={() => handleDeactivateDistGroup(g.id, g.name)}
                      style={{ padding: '4px 8px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}
                    >
                      Deactivate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* TAB 4: Reporting Tools & Closure Rules */}
      {activeTab === 'reporting' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* Relate Reports Form */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '18px' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>
              Relate / Link Reports (Master ↔ Child)
            </h3>
            <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 14px 0' }}>
              Link duplicate or auxiliary safety reports to a primary master incident. Circular relations are strictly blocked.
            </p>
            <form onSubmit={handleRelateReports}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Master Report ID *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. REP-101"
                  value={relateForm.master_report_id}
                  onChange={(e) => setRelateForm({ ...relateForm, master_report_id: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Related (Child) Report ID *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. REP-102"
                  value={relateForm.related_report_id}
                  onChange={(e) => setRelateForm({ ...relateForm, related_report_id: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Reason & Justification *</label>
                <textarea
                  required
                  rows={2}
                  placeholder="e.g. Same bird strike event reported independently by cabin crew"
                  value={relateForm.reason_comment}
                  onChange={(e) => setRelateForm({ ...relateForm, reason_comment: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                style={{ background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
              >
                Link Reports
              </button>
            </form>
          </div>

          {/* Taxonomy Settings Copy */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '18px' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>
              Clone Event Classification Settings
            </h3>
            <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 14px 0' }}>
              Copy ADREP, ECCAIRS taxonomy codes, IDX presets, and mandatory flags from a source classification to target.
            </p>
            <form onSubmit={handleCopyClassification}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Source Classification ID *</label>
                <input
                  type="number"
                  required
                  placeholder="e.g. 1"
                  value={copyForm.source_classification_id}
                  onChange={(e) => setCopyForm({ ...copyForm, source_classification_id: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Target Classification ID *</label>
                <input
                  type="number"
                  required
                  placeholder="e.g. 2"
                  value={copyForm.target_classification_id}
                  onChange={(e) => setCopyForm({ ...copyForm, target_classification_id: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                style={{ background: '#0284c7', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
              >
                Clone Taxonomy Settings
              </button>
            </form>

            <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                Report Closure Enforcement Policy
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="closureOpt"
                    checked={closureOption === 1}
                    onChange={() => setClosureOption(1)}
                  />
                  <span><strong>Option 1 (Strict Interlocking):</strong> All corrective actions (CAP) and investigations must be CLOSED before report closure.</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="closureOpt"
                    checked={closureOption === 2}
                    onChange={() => setClosureOption(2)}
                  />
                  <span><strong>Option 2 (Root Cause Required):</strong> Root cause analysis must be recorded before closure.</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="closureOpt"
                    checked={closureOption === 3}
                    onChange={() => setClosureOption(3)}
                  />
                  <span><strong>Option 3 (Flexible):</strong> Reports can be closed with pending actions based on manager discretion.</span>
                </label>
              </div>
              <button
                type="button"
                onClick={handleSaveClosureOption}
                disabled={loading}
                style={{ background: '#10b981', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
              >
                Save Closure Policy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TAB 5: Admin Audit Trail */}
      {activeTab === 'audit' && (
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '18px' }}>
          <h3 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>
            System Administrator Audit Logs
          </h3>
          <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 14px 0' }}>
            Append-only historical evidence of administrative configurations, role assignments, and taxonomy modifications.
          </p>

          <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#475569', position: 'sticky', top: 0, zIndex: 10 }}>
                  <SortableTh field="timestampUtc" currentField={auditSortField} currentDirection={auditSortDir} onSort={toggleAuditSort} style={{ padding: '10px' }}>
                    Timestamp
                  </SortableTh>
                  <SortableTh field="adminUserId" currentField={auditSortField} currentDirection={auditSortDir} onSort={toggleAuditSort} style={{ padding: '10px' }}>
                    Admin Actor
                  </SortableTh>
                  <SortableTh field="entityType" currentField={auditSortField} currentDirection={auditSortDir} onSort={toggleAuditSort} style={{ padding: '10px' }}>
                    Entity Type
                  </SortableTh>
                  <SortableTh field="action" currentField={auditSortField} currentDirection={auditSortDir} onSort={toggleAuditSort} style={{ padding: '10px' }}>
                    Action
                  </SortableTh>
                  <th style={{ padding: '10px' }}>Change Details</th>
                </tr>
              </thead>
              <tbody>
                {sortedAdminAudit.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>
                      No administrative audit records logged yet.
                    </td>
                  </tr>
                ) : (
                  sortedAdminAudit.map((log) => (
                    <tr key={log.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px', fontSize: '12px', color: '#64748b' }}>
                        {log.timestampUtc ? new Date(log.timestampUtc).toLocaleString() : '—'}
                      </td>
                      <td style={{ padding: '10px', fontWeight: 600, color: '#1e293b' }}>{log.adminUserId}</td>
                      <td style={{ padding: '10px' }}>
                        <span style={{ background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                          {log.entityType}
                        </span>
                      </td>
                      <td style={{ padding: '10px', fontWeight: 700, color: '#2563eb' }}>{log.action}</td>
                      <td style={{ padding: '10px', fontSize: '12px', color: '#334155' }}>
                        <pre style={{ margin: 0, fontSize: '11px', background: '#f8fafc', padding: '4px 8px', borderRadius: '4px', maxWidth: '300px', overflowX: 'auto' }}>
                          {JSON.stringify(log.newValue || log.oldValue || {}, null, 1)}
                        </pre>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Role Assignment Modal */}
      {showRoleModal && selectedUser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#ffffff', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '540px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                Assign Roles: {selectedUser.username}
              </h3>
              <button onClick={() => setShowRoleModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ maxHeight: '350px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '18px' }}>
              {roles.map((r) => {
                const isSelected = selectedRoles.includes(r.roleKey);
                return (
                  <div
                    key={r.roleKey}
                    onClick={() => toggleRoleSelection(r.roleKey)}
                    style={{
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: isSelected ? '2px solid #2563eb' : '1px solid #cbd5e1',
                      background: isSelected ? '#eff6ff' : '#ffffff',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '13px', color: '#1e293b' }}>{r.name}</div>
                      <div style={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace' }}>{r.roleKey} • {r.module}</div>
                    </div>
                    {isSelected && <Check size={18} color="#2563eb" />}
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                onClick={() => setShowRoleModal(false)}
                style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveRoles}
                disabled={loading}
                style={{ padding: '8px 16px', background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
              >
                Save Role Allocations
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Aircraft Modal */}
      {showAddAircraftModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#ffffff', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '440px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>Register New Aircraft</h3>
            <form onSubmit={handleCreateAircraft}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Registration (ICAO) *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. TC-SAF"
                  value={newAircraft.registration}
                  onChange={(e) => setNewAircraft({ ...newAircraft, registration: e.target.value.toUpperCase() })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Aircraft Type / Airframe *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Airbus A320-200"
                  value={newAircraft.aircraft_type}
                  onChange={(e) => setNewAircraft({ ...newAircraft, aircraft_type: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Serial Number</label>
                <input
                  type="text"
                  placeholder="e.g. MSN-4592"
                  value={newAircraft.serial_number}
                  onChange={(e) => setNewAircraft({ ...newAircraft, serial_number: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Operator</label>
                <input
                  type="text"
                  placeholder="e.g. Turkish Airlines"
                  value={newAircraft.operator_name}
                  onChange={(e) => setNewAircraft({ ...newAircraft, operator_name: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddAircraftModal(false)}
                  style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{ padding: '8px 16px', background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Register Aircraft
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Distribution Group Modal */}
      {showAddGroupModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#ffffff', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '440px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>New Distribution Group</h3>
            <form onSubmit={handleCreateDistGroup}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Group Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Flight Safety Committee"
                  value={newGroup.name}
                  onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Description</label>
                <textarea
                  rows={2}
                  placeholder="Routing scope and purpose"
                  value={newGroup.description}
                  onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddGroupModal(false)}
                  style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{ padding: '8px 16px', background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Create Group
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showCreateUserModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#ffffff', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '440px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>Create System User</h3>
            <form onSubmit={handleCreateUser}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Username *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. captain.smith"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Email *</label>
                <input
                  type="email"
                  required
                  placeholder="e.g. smith@airline.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '18px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>First Name</label>
                  <input
                    type="text"
                    value={newUser.first_name}
                    onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                    style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Last Name</label>
                  <input
                    type="text"
                    value={newUser.last_name}
                    onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                    style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setShowCreateUserModal(false)}
                  style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{ padding: '8px 16px', background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
